/**
 * schema.ts — Schema.org JSON-LD builder helpers for SARGO.
 *
 * All builders return plain objects — pass directly to <SEO schema={...} />.
 * PageHelmet serialises them via JSON.stringify into <script type="application/ld+json">.
 *
 * To combine multiple schemas on one page wrap them in an array:
 *   schema={[buildOrganization(baseUrl), buildBreadcrumbs(baseUrl, items)]}
 */

// ─── Shared constants ──────────────────────────────────────────────────────────

/**
 * Logo path relative to site root.
 * Stored in /public/brand/logo.png — stable, versioned with the build.
 * Call getLogoUrl(baseUrl) to get the absolute URL.
 */
export const SARGO_LOGO_PATH = '/brand/logo.png';

export function getLogoUrl(baseUrl: string): string {
  return `${baseUrl}${SARGO_LOGO_PATH}`;
}

export const SARGO_OG_IMAGE = '/brand/5.png';

// ─── Organization ──────────────────────────────────────────────────────────────

export function buildOrganization(baseUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'SARGO',
    url: baseUrl,
    description:
      'Производитель SPC-ламината, SPC-панелей и ковровой плитки из Узбекистана.',
    email: 'info@sargogroup.com',
    telephone: '+998702240004',
    logo: {
      '@type': 'ImageObject',
      url: getLogoUrl(baseUrl),
      width: 300,
      height: 60,
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '270 Spitamen Shoh Street',
      addressLocality: 'Samarkand',
      addressCountry: 'UZ',
    },
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      minValue: 100,
    },
    sameAs: [
      'https://www.instagram.com/sargo.uz/',
      'https://www.tiktok.com/@sargo.uz',
      // LinkedIn: numeric company ID is the stable canonical public URL
      'https://www.linkedin.com/company/110521447/',
      'https://www.youtube.com/@sargo.company',
    ],
  };
}

// ─── BreadcrumbList ────────────────────────────────────────────────────────────

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function buildBreadcrumbs(baseUrl: string, items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`,
    })),
  };
}

// ─── CollectionPage ────────────────────────────────────────────────────────────

export function buildCollectionPage(baseUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Каталог продукции SARGO',
    description:
      'Каталог продукции SARGO: SPC-ламинат, SPC-панели и ковровая плитка.',
    url: `${baseUrl}/catalog`,
    publisher: buildPublisher(baseUrl),
    about: [
      { '@type': 'Product', name: 'SPC Laminate' },
      { '@type': 'Product', name: 'SPC Wall Panels' },
      { '@type': 'Product', name: 'Carpet Tile' },
    ],
  };
}

// ─── Article ──────────────────────────────────────────────────────────────────

export function buildArticle(params: {
  headline: string;
  description: string;
  url: string;
  baseUrl: string;
  datePublished?: string;
}) {
  const publisher = buildPublisher(params.baseUrl);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: params.headline,
    description: params.description,
    url: params.url.startsWith('http')
      ? params.url
      : `${params.baseUrl}${params.url}`,
    ...(params.datePublished ? { datePublished: params.datePublished } : {}),
    author: publisher,
    publisher,
  };
}

// ─── FAQPage ──────────────────────────────────────────────────────────────────

export interface FAQSchemaItem {
  question: string;
  answer: string;
}

/**
 * Build a standalone FAQPage schema.
 * Pass only items where both question AND answer are non-empty strings.
 * Add this as a separate entry in the schema array alongside Article + BreadcrumbList.
 */
export function buildFAQPage(items: FAQSchemaItem[]) {
  const validItems = items.filter(
    (item) => item.question?.trim() && item.answer?.trim()
  );
  if (validItems.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: validItems.map((item) => ({
      '@type': 'Question',
      name: item.question.trim(),
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer.trim(),
      },
    })),
  };
}

// ─── Shared publisher (Organization stub used inside Article/CollectionPage) ───

function buildPublisher(baseUrl: string) {
  return {
    '@type': 'Organization',
    name: 'SARGO',
    logo: {
      '@type': 'ImageObject',
      url: getLogoUrl(baseUrl),
      width: 300,
      height: 60,
    },
  };
}
